import { DictionaryConfig } from "@/containers/dictionaries/types";
import { ReadonlyObject } from "@/types/readOnly";
import { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { Context, createContext, useContext } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";

interface DictionaryRecordContextType<
  TRead extends ReadonlyObject,
  TWrite extends FieldValues,
> {
  id: string;
  config: DictionaryConfig<TRead, unknown>;
  query?: UseQueryResult<TRead, Error>;
  mutation: UseMutationResult<TRead, Error, TWrite, unknown>;
  form: UseFormReturn<TWrite>;
  deleteMutation?: UseMutationResult<unknown, Error, unknown, unknown>;
}

function createGenericContext<T>() {
  return createContext<T | undefined>(undefined);
}

const DictionaryRecordContext: Context<
  DictionaryRecordContextType<ReadonlyObject, FieldValues> | undefined
> =
  createGenericContext<
    DictionaryRecordContextType<ReadonlyObject, FieldValues>
  >();

export const DictionaryRecordContextProvider = <
  TRead extends ReadonlyObject,
  TWrite extends FieldValues,
>({
  children,
  value,
}: React.PropsWithChildren<{
  value: DictionaryRecordContextType<TRead, TWrite>;
}>) => {
  // Here we know that TRead extends ReadonlyObject and TWrite extends FieldValues,
  // so this cast is safe
  const typedValue = value as unknown as DictionaryRecordContextType<
    ReadonlyObject,
    FieldValues
  >;

  return (
    <DictionaryRecordContext.Provider value={typedValue}>
      {children}
    </DictionaryRecordContext.Provider>
  );
};

export const useDictionaryContext = <
  TRead extends ReadonlyObject,
  TWrite extends FieldValues,
>(): DictionaryRecordContextType<TRead, TWrite> => {
  const context = useContext(DictionaryRecordContext);
  if (context === undefined) {
    throw new Error(
      "DictionaryContextConsumer must be used within a DictionaryContextProvider",
    );
  }

  return context as unknown as DictionaryRecordContextType<TRead, TWrite>;
};
